import { useEffect } from "react";
import { usePavilion } from '../contexts/PavilionContext';

export const EULA = () => {

  const isPavilionMode = true;

  useEffect(() => {
    window.scrollTo(0, 0);
  });

  return isPavilionMode ? (
    <div className='bg-content text-content-text m-0 h-auto w-full p-0'>
      <div className='bg-hero bg-content text-content-text m-0 h-auto w-full bg-cover bg-no-repeat p-0'>
        <div className='container mx-auto h-auto w-full px-8 pb-8 pt-20'>
          <section id='hero' className='grid grid-cols-12 gap-x-4 gap-y-8'>
            <div className='grid-rows col-span-full grid content-center gap-y-4 lg:gap-y-8'>
              <h1 className='row-auto text-3xl font-bold lg:text-4xl'>End User License Agreement</h1>
              <p className='row-auto'>Last updated: June 11, 2025</p>
              <p className='row-auto'>Version: 1.0</p>
              <br />
            </div>
          </section>
        </div>
      </div>

      <div className='bg-content-secondary text-content-text m-0 h-auto w-full'>
        <div className='container mx-auto h-auto w-full px-0 py-20'>
          <section className='grid grid-cols-12 gap-10 md:gap-8'>
            <p className='col-span-full -mt-4'>
              <strong>Playcast Desktop Game Streaming Software</strong>
            </p>

            <p className='col-span-full -mt-4'>
              <strong>IMPORTANT - READ CAREFULLY:</strong> This End User License Agreement ("Agreement" or "EULA") is a legal agreement between
              you (either an individual or a single entity) ("You" or "User") and Playcast Inc. ("Company," "we," "us," or "our") for the
              Playcast desktop streaming software application, including any associated drivers, updates, and documentation (collectively,
              the "Software").
            </p>

            <p className='col-span-full -mt-4'>
              BY INSTALLING, COPYING, OR OTHERWISE USING THE SOFTWARE, YOU AGREE TO BE BOUND BY THE TERMS OF THIS AGREEMENT. IF YOU DO NOT
              AGREE TO THE TERMS OF THIS AGREEMENT, DO NOT INSTALL OR USE THE SOFTWARE.
            </p>

            <p className='col-span-full -mt-4'>
              <strong>IMPORTANT:</strong> Your use of Playcast's online Service is governed by our separate Terms of Service, which can be
              found at www.playcast.io. This EULA governs only the downloadable Software components.
            </p>

            <h2 className='col-span-full text-3xl'>1. License Grant and Eligibility</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>1.1 Age Requirement</h3>
            <p className='col-span-full'>
              You must be at least 18 years old to use the Software. By agreeing to this EULA, you represent and warrant that you are at
              least 18 years old and have not previously been suspended or removed from any Playcast services.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>1.2 Limited License</h3>
            <p className='col-span-full'>
              Subject to your compliance with this Agreement, Company grants you a limited, non-exclusive, non-transferable, revocable
              license to install and use the Software on devices you own or control solely for your personal, non-commercial use.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>1.3 Driver Installation</h3>
            <p className='col-span-full'>
              You acknowledge and agree that the Software requires the installation of system-level drivers and components to enable game
              and application streaming functionality. You expressly consent to the installation, operation, and automatic updating of
              these drivers.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>1.4 Restrictions</h3>
            <p className='col-span-full'>
              You may not:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Distribute, sublicense, rent, lease, or lend the Software</li>
              <li>Reverse engineer, decompile, or disassemble the Software</li>
              <li>Remove or modify any proprietary notices or labels</li>
              <li>Use the Software for commercial purposes without explicit written permission</li>
              <li>Circumvent or disable any security features or usage restrictions</li>
              <li>Use the Software in violation of our Terms of Service or for any illegal purpose</li>
            </ul>

            <h2 className='col-span-full text-3xl'>2. System Requirements and Compatibility</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>2.1 System Modifications</h3>
            <p className='col-span-full'>
              You acknowledge that the Software makes significant modifications to your computer system, including but not limited to:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Installation of kernel-level drivers</li>
              <li>Network configuration changes</li>
              <li>Graphics and audio system modifications</li>
              <li>Registry and system file modifications</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>2.2 Compatibility Disclaimer</h3>
            <p className='col-span-full'>
              Company does not guarantee compatibility with all hardware configurations, operating systems, or third-party software. You
              are responsible for ensuring your system meets minimum requirements.
            </p>

            <h2 className='col-span-full text-3xl'>3. Streaming and Content Responsibilities</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>3.1 Content Compliance</h3>
            <p className='col-span-full'>
              You are solely responsible for ensuring that any games, applications, or content you stream through the Software comply with:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>All applicable third-party End User License Agreements</li>
              <li>Copyright and intellectual property laws</li>
              <li>Platform-specific terms of service</li>
              <li>Local, state, and federal regulations</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>3.2 Third-Party EULA Compliance</h3>
            <p className='col-span-full'>
              You acknowledge that streaming games or applications may violate their respective EULAs. You agree to review and comply with
              all applicable third-party agreements before streaming any content.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>3.3 Prohibited Content</h3>
            <p className='col-span-full'>
              You may not use the Software to stream:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Illegal, harmful, or offensive content</li>
              <li>Content that violates third-party intellectual property rights</li>
              <li>Content that violates any applicable laws or regulations</li>
            </ul>

            <h2 className='col-span-full text-3xl'>4. Data Collection and Privacy</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>4.1 Data Collection</h3>
            <p className='col-span-full'>
              You acknowledge and consent to Company's collection, processing, and storage of:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>System performance and hardware information</li>
              <li>Usage statistics and streaming metrics</li>
              <li>Error logs and diagnostic information</li>
              <li>Network and connection data</li>
              <li>Game and application usage patterns</li>
              <li>Information described in our Privacy Policy</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>4.2 Analytics and Improvement</h3>
            <p className='col-span-full'>
              Company may use collected data to:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Improve Software performance and features</li>
              <li>Provide technical support</li>
              <li>Develop new features and services</li>
              <li>Generate anonymized analytics and reports</li>
              <li>Provide customized content and functionality</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>4.3 Privacy Policy</h3>
            <p className='col-span-full'>
              Your use of the Software is also governed by our Privacy Policy, which can be found at www.playcast.io and is incorporated
              herein by reference. The Privacy Policy explains how we collect, use, and protect your information, including automatic data
              collection and sharing with third-party services.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>4.4 International Data Transfers</h3>
            <p className='col-span-full'>
              You acknowledge that information collected by the Software may be stored and processed in the United States or other countries
              where Company or its service providers maintain facilities.
            </p>

            <h2 className='col-span-full text-3xl'>5. Prohibited Conduct</h2>
            <p className='col-span-full'>
              In addition to the restrictions in Section 1.4, you agree not to use the Software in any manner that violates the Prohibited
              Conduct provisions of our Terms of Service. This includes but is not limited to using the Software for illegal purposes,
              harassing other users, violating intellectual property rights, or interfering with the operation of our services. The complete
              list of prohibited conduct can be found in our Terms of Service at www.playcast.io.
            </p>

            <h2 className='col-span-full text-3xl'>6. Security and System Risks</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>6.1 Security Acknowledgment</h3>
            <p className='col-span-full'>
              You acknowledge that the Software:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Creates network connections that may expose your system to security risks</li>
              <li>Installs system-level components that may affect system security</li>
              <li>Enables remote access to your games and applications</li>
              <li>May introduce vulnerabilities inherent in streaming technology</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>6.2 Third-Party Computer Usage</h3>
            <p className='col-span-full'>
              <strong>IMPORTANT DISCLAIMER</strong>: When using Playcast's online services in conjunction with this Software, you understand
              and acknowledge that you may be playing games hosted on other users' personal computers. You expressly understand and agree that:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Playcast does not own, control, or monitor these third-party computers</li>
              <li>Playcast cannot guarantee the security, privacy, or integrity of third-party systems</li>
              <li>You assume all risks associated with accessing games on other users' computers</li>
              <li>Other users' computers may have different security configurations and vulnerabilities</li>
              <li>Playcast disclaims all responsibility for any security breaches, data loss, or other issues arising from use of third-party computers</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>6.3 Security Responsibility</h3>
            <p className='col-span-full'>
              You are solely responsible for:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Maintaining appropriate security measures on your system</li>
              <li>Monitoring and controlling access to your streaming sessions</li>
              <li>Protecting sensitive information during streaming</li>
              <li>Regular system updates and security patches</li>
              <li>Understanding the risks of distributed gaming platforms</li>
            </ul>

            <h2 className='col-span-full text-3xl'>7. Limitation of Liability and Indemnification</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>7.1 Disclaimer of Warranties</h3>
            <p className='col-span-full'>
              THE SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. COMPANY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED,
              INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>7.2 Limitation of Liability</h3>
            <p className='col-span-full'>
              IN NO EVENT SHALL COMPANY BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING
              BUT NOT LIMITED TO:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>System damage or data loss</li>
              <li>Security breaches or unauthorized access</li>
              <li>Third-party claims related to streamed content</li>
              <li>Business interruption or lost profits</li>
              <li>Hardware damage or malfunction</li>
            </ul>
            <p className='col-span-full'>
              COMPANY'S TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU FOR THE SOFTWARE IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>7.3 User Indemnification</h3>
            <p className='col-span-full'>
              You agree to indemnify, defend, and hold harmless Company from and against any claims, damages, losses, or expenses arising from:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Your use of the Software</li>
              <li>Your violation of this Agreement or our Terms of Service</li>
              <li>Your violation of third-party rights or agreements</li>
              <li>Content you stream through the Software</li>
              <li>Any system damage or security issues resulting from Software use</li>
            </ul>

            <h2 className='col-span-full text-3xl'>8. System Impact and User Acknowledgments</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>8.1 Performance Impact</h3>
            <p className='col-span-full'>
              You acknowledge that the Software may:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Affect system performance and resource usage</li>
              <li>Interfere with other applications or drivers</li>
              <li>Require significant network bandwidth</li>
              <li>Impact battery life on mobile devices</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>8.2 System Changes</h3>
            <p className='col-span-full'>
              You acknowledge and accept responsibility for any system changes, including:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Driver conflicts or compatibility issues</li>
              <li>System instability or crashes</li>
              <li>Network configuration changes</li>
              <li>Registry or system file modifications</li>
            </ul>

            <h2 className='col-span-full text-3xl'>9. Automatic Updates</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>9.1 Update Consent</h3>
            <p className='col-span-full'>
              You agree that the Software may automatically download and install updates, including driver updates, without prior notice.
              These updates are subject to this Agreement.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>9.2 Update Requirements</h3>
            <p className='col-span-full'>
              Some updates may be required for continued use of the Software. Failure to install required updates may result in loss of
              functionality.
            </p>

            <h2 className='col-span-full text-3xl'>10. Termination</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>10.1 Termination Rights</h3>
            <p className='col-span-full'>
              This Agreement is effective until terminated. Company may terminate this Agreement immediately if you violate any terms or if
              your access to Playcast services is terminated under our Terms of Service. You may terminate by uninstalling the Software.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>10.2 Effect of Termination</h3>
            <p className='col-span-full'>
              Upon termination:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Your license rights immediately cease</li>
              <li>You must cease all use of the Software</li>
              <li>Certain data collection practices may continue as outlined in the Privacy Policy</li>
              <li>Installed drivers may remain unless manually removed</li>
            </ul>

            <h2 className='col-span-full text-3xl'>11. Third-Party Services and Integrations</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>11.1 Third-Party Dependencies</h3>
            <p className='col-span-full'>
              The Software may integrate with or depend on third-party services, platforms, or APIs. Company is not responsible for the
              availability, functionality, or terms of such third-party services.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>11.2 Platform Compliance</h3>
            <p className='col-span-full'>
              You are responsible for ensuring your use of the Software complies with the terms of service of any gaming platforms, digital
              distribution services, or other third-party services you access through the Software.
            </p>

            <h2 className='col-span-full text-3xl'>12. Export Compliance</h2>
            <p className='col-span-full'>
              You acknowledge that the Software may be subject to export control laws and regulations. You agree to comply with all
              applicable export and import laws and regulations.
            </p>

            <h2 className='col-span-full text-3xl'>13. Dispute Resolution</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>13.1 Arbitration</h3>
            <p className='col-span-full'>
              Any disputes arising from this Agreement are subject to the dispute resolution and arbitration provisions set forth in our
              Terms of Service, including the binding arbitration requirement and class action waiver. The complete arbitration procedures
              can be found at www.playcast.io.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>13.2 Governing Law</h3>
            <p className='col-span-full'>
              This Agreement shall be governed by the laws of the State of Delaware, without regard to conflict of law principles,
              consistent with our Terms of Service.
            </p>

            <h2 className='col-span-full text-3xl'>14. General Provisions</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>14.1 Entire Agreement</h3>
            <p className='col-span-full'>
              This Agreement, together with our Terms of Service and Privacy Policy, constitutes the entire agreement between the parties
              regarding the Software.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>14.2 Relationship to Terms of Service</h3>
            <p className='col-span-full'>
              This EULA supplements our Terms of Service. In case of conflicts between this EULA and the Terms of Service regarding the
              Software specifically, this EULA controls. For all other matters, the Terms of Service controls.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>14.3 Severability</h3>
            <p className='col-span-full'>
              If any provision of this Agreement is found to be unenforceable, the remainder shall remain in full force and effect.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>14.4 No Waiver</h3>
            <p className='col-span-full'>
              Company's failure to enforce any provision shall not constitute a waiver of such provision or any other provision.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>14.5 Assignment</h3>
            <p className='col-span-full'>
              You may not assign this Agreement without Company's prior written consent. Company may assign this Agreement without restriction.
            </p>

            <h2 className='col-span-full text-3xl'>15. Contact Information</h2>
            <p className='col-span-full'>
              For questions regarding this Agreement, please contact:
            </p>
            <p className='col-span-full'>
              Playcast Inc.<br />
              1000 SE Everett Mall Way #40p<br />
              Everett, WA 98208<br />
              Email: support@playcast.io<br />
              Privacy-related inquiries: privacy@playcast.io
            </p>

            <h2 className='col-span-full text-3xl'>16. Acknowledgment</h2>
            <p className='col-span-full'>
              BY CLICKING "I AGREE" OR BY INSTALLING OR USING THE SOFTWARE, YOU ACKNOWLEDGE THAT YOU HAVE READ THIS AGREEMENT, UNDERSTAND
              IT, AND AGREE TO BE BOUND BY ITS TERMS AND CONDITIONS.
            </p>

            <h2 className='col-span-full text-3xl mt-8'>PLAYCAST PAVILION™ SOFTWARE ADDENDUM</h2>
            <h3 className='col-span-full text-2xl font-bold mt-4'>End User License Agreement Addendum</h3>

            <p className='col-span-full -mt-4'>
              <strong>IMPORTANT:</strong> This Addendum supplements and modifies the main End User License Agreement ("Main EULA") for users who elect to enable Playcast Pavilion™ software functionality. By enabling Playcast Pavilion™ features, you agree to the additional terms below in addition to all terms in the Main EULA and our Terms of Service.
            </p>

            <p className='col-span-full -mt-4'>
              <em>This Addendum is part of the software license agreement with Playcast Inc. Use of Playcast Pavilion™ also requires compliance with our Terms of Service at www.playcast.io.</em>
            </p>

            <p className='col-span-full -mt-4'>
              <strong>ELIGIBILITY:</strong> You must be at least 18 years old to use Playcast Pavilion™ features, consistent with our Terms of Service requirements.
            </p>

            <h2 className='col-span-full text-3xl mt-8'>1. PLAYCAST PAVILION™ SERVICE OVERVIEW</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>1.1 Service Description</h3>
            <p className='col-span-full'>
              Playcast Pavilion™ is a distributed gaming platform that utilizes your computer's idle resources to host gaming sessions for other users in our network. When you enable Playcast Pavilion™, your computer becomes part of our distributed hosting infrastructure.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>1.2 Critical Understanding - Distributed Gaming Risks</h3>
            <p className='col-span-full'>
              <strong>YOU EXPRESSLY ACKNOWLEDGE AND UNDERSTAND</strong> that Playcast Pavilion™ enables you to:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li><strong>Play games hosted on other users' personal computers</strong> that Playcast does not own, control, or monitor</li>
              <li><strong>Host games on your computer for other users</strong> to access remotely</li>
              <li><strong>Share computing resources</strong> in a peer-to-peer gaming network</li>
            </ul>

            <p className='col-span-full mt-4'>
              <strong>YOU ASSUME ALL RISKS</strong> associated with this distributed model, including but not limited to security vulnerabilities, data exposure, system instability, and privacy concerns that may arise from playing games on third-party computers or hosting games for others.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>1.3 Voluntary Participation</h3>
            <p className='col-span-full'>
              Playcast Pavilion™ functionality is entirely optional. You may choose to enable or disable Playcast Pavilion™ features, but access to hosted games through our service requires maintaining active Playcast Pavilion™ participation.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>1.4 Resource Sharing Agreement</h3>
            <p className='col-span-full'>
              By enabling Playcast Pavilion™, you agree to share your computer's idle processing power, network bandwidth, and storage capacity to host gaming sessions for other users in exchange for access to games hosted on other users' systems.
            </p>

            <h2 className='col-span-full text-3xl mt-8'>2. SYSTEM ACCESS AND MODIFICATIONS</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>2.1 Account Creation Authority</h3>
            <p className='col-span-full'>
              You grant Company explicit permission to:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Create and manage user accounts on your local system</li>
              <li>Configure account permissions and access rights</li>
              <li>Install and configure user profiles for gaming services</li>
              <li>Modify account settings as necessary for service operation</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>2.2 Game Service Installation</h3>
            <p className='col-span-full'>
              Company is authorized to:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Install game clients, launchers, and related software in created accounts</li>
              <li>Download, install, and update games and game services</li>
              <li>Configure gaming platforms and digital distribution clients</li>
              <li>Manage game libraries and installations across accounts</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>2.3 System Resource Usage</h3>
            <p className='col-span-full'>
              You acknowledge and consent to Company's use of:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>CPU processing power during idle periods</li>
              <li>Available RAM and system memory</li>
              <li>Network bandwidth for gaming sessions and data transfer</li>
              <li>Storage space for game installations and temporary files</li>
              <li>Graphics processing capabilities for game rendering</li>
            </ul>

            <h2 className='col-span-full text-3xl mt-8'>3. NETWORK ACCESS AND VPN REQUIREMENTS</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>3.1 VPN Connection Mandate</h3>
            <p className='col-span-full'>
              All Playcast Pavilion™ operations require connection to Company's private VPN network. You agree that:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Your system will automatically connect to our VPN when hosting sessions</li>
              <li>All gaming traffic will be routed through our secure network infrastructure</li>
              <li>VPN connection is mandatory for licensing compliance and security</li>
              <li>Company may monitor and log VPN usage for compliance purposes</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>3.2 Network Compliance</h3>
            <p className='col-span-full'>
              The VPN requirement ensures:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Compliance with game publisher licensing terms</li>
              <li>Protection of intellectual property rights</li>
              <li>Secure data transmission between users</li>
              <li>Proper geographic licensing compliance</li>
              <li>Prevention of unauthorized game access</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>3.3 Network Access Rights</h3>
            <p className='col-span-full'>
              You grant Company the right to:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Route all gaming traffic through our VPN infrastructure</li>
              <li>Monitor network usage for compliance and security purposes</li>
              <li>Block or filter network traffic as necessary for legal compliance</li>
              <li>Implement geographic restrictions based on game licensing requirements</li>
            </ul>

            <h2 className='col-span-full text-3xl'>4. PRIVACY AND DATA IMPLICATIONS OF DISTRIBUTED GAMING</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>4.1 Third-Party Computer Data Exposure</h3>
            <p className='col-span-full'>
              When playing games on other users' computers through Playcast Pavilion™, you acknowledge that:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Your gaming behavior and inputs may be visible to the host computer owner</li>
              <li>Game data and save files may be stored on third-party systems</li>
              <li>Your IP address and network information may be exposed to host systems</li>
              <li>Host computer owners may have access to logs of your gaming activity</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>4.2 Data Collection in Distributed Environment</h3>
            <p className='col-span-full'>
              In addition to data collection described in our Privacy Policy, Playcast Pavilion™ may collect:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Cross-system performance metrics and compatibility data</li>
              <li>Distributed session quality and latency information</li>
              <li>Host-guest interaction patterns and usage analytics</li>
              <li>Network routing and VPN connection data</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>4.3 Host Computer Data Exposure</h3>
            <p className='col-span-full'>
              When hosting games for other users, you acknowledge that:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Guest user activities will be processed on your computer</li>
              <li>Game installation and configuration data will be created on your system</li>
              <li>Network traffic from other users will pass through your system</li>
              <li>Your system's IP address may be visible to Playcast's infrastructure</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>4.4 Privacy Policy Application</h3>
            <p className='col-span-full'>
              All data collection and usage practices are governed by our Privacy Policy at www.playcast.io, including data sharing with
              third-party analytics providers and international data transfers.
            </p>

            <h2 className='col-span-full text-3xl'>5. CONDITIONAL ACCESS MODEL</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>5.1 Reciprocal Hosting Requirement</h3>
            <p className='col-span-full'>
              Your access to games hosted on other users' systems is conditioned upon your willingness to host games for other users.
              You understand that:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Game access is tied to active Playcast Pavilion™ participation</li>
              <li>Disabling hosting capabilities may result in loss of access to hosted games</li>
              <li>Company maintains the right to verify hosting participation</li>
              <li>Extended periods of non-participation may result in service suspension</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>5.2 Idle Time Utilization</h3>
            <p className='col-span-full'>
              You agree that during periods when your computer is idle:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Company may initiate gaming sessions for other users</li>
              <li>Your system resources may be fully utilized for hosting</li>
              <li>Gaming sessions may continue until you actively use your computer</li>
              <li>You will receive priority access to your own system when needed</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>5.3 Access Revocation Rights</h3>
            <p className='col-span-full'>
              Company reserves the absolute right to:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Immediately revoke your access to hosted games</li>
              <li>Suspend or terminate your Playcast Pavilion™ participation</li>
              <li>Remove installed games and services from your system</li>
              <li>Disable created accounts and associated data</li>
              <li>Take such action without prior notice or explanation</li>
            </ul>

            <h2 className='col-span-full text-3xl'>6. USER CONDUCT AND SECURITY OBLIGATIONS</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>6.1 General Conduct Requirements</h3>
            <p className='col-span-full'>
              In addition to the specific prohibitions below, you must comply with all Prohibited Conduct provisions in our Terms of
              Service, which apply to all use of Playcast Pavilion™. This includes prohibitions against illegal activities, harassment,
              intellectual property violations, and system interference.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>6.2 Prohibited Malicious Activities</h3>
            <p className='col-span-full'>
              You expressly agree NOT to engage in any of the following activities when other users are accessing games on your system:
            </p>

            <h4 className='col-span-full text-lg font-bold mt-4'>6.2.1 Data Theft and Privacy Violations</h4>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Attempting to capture, record, or access other users' passwords</li>
              <li>Installing keyloggers or other monitoring software</li>
              <li>Accessing other users' personal files or data</li>
              <li>Attempting to extract or copy user credentials</li>
              <li>Monitoring or recording user communications</li>
            </ul>

            <h4 className='col-span-full text-lg font-bold mt-4'>6.2.2 Input Interference and Manipulation</h4>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Interfering with other users' game inputs or controls</li>
              <li>Injecting false inputs or commands</li>
              <li>Disrupting game sessions through input manipulation</li>
              <li>Altering or intercepting user commands</li>
              <li>Creating lag or delay in user inputs</li>
            </ul>

            <h4 className='col-span-full text-lg font-bold mt-4'>6.2.3 Unauthorized Communication and Messaging</h4>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Injecting unauthorized messages into game chat</li>
              <li>Sending unsolicited communications to players</li>
              <li>Impersonating other users or administrators</li>
              <li>Broadcasting unauthorized advertisements or content</li>
              <li>Interfering with legitimate game communications</li>
            </ul>

            <h4 className='col-span-full text-lg font-bold mt-4'>6.2.4 Session Disruption and Sabotage</h4>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Intentionally causing game crashes or failures</li>
              <li>Modifying game files during active sessions</li>
              <li>Disrupting network connections for hosted sessions</li>
              <li>Installing unauthorized modifications or cheats</li>
              <li>Degrading game performance intentionally</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>6.3 Security Monitoring</h3>
            <p className='col-span-full'>
              You acknowledge that Company may:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Monitor your system for malicious activity</li>
              <li>Log user interactions during hosted sessions</li>
              <li>Implement security measures to protect other users</li>
              <li>Automatically detect and prevent prohibited activities</li>
              <li>Share security incident information with law enforcement</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>6.4 Immediate Termination for Violations</h3>
            <p className='col-span-full'>
              Any violation of Section 6.2 will result in:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Immediate termination of your Playcast Pavilion™ access</li>
              <li>Removal of all Company software from your system</li>
              <li>Permanent ban from all Company services</li>
              <li>Potential legal action for damages caused</li>
              <li>Reporting of criminal activity to appropriate authorities</li>
            </ul>

            <h2 className='col-span-full text-3xl'>7. ENHANCED LIABILITY AND INDEMNIFICATION</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>7.1 User Liability for Guest Sessions</h3>
            <p className='col-span-full'>
              You are fully liable for:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Any damage caused to other users' gaming experiences</li>
              <li>Security breaches originating from your system</li>
              <li>Malicious activities conducted from your computer</li>
              <li>Unauthorized access to other users' data or accounts</li>
              <li>Any violations of game publishers' terms of service</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>7.2 Enhanced Liability for Distributed Gaming Risks</h3>
            <p className='col-span-full'>
              You specifically acknowledge and accept liability for:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Any security vulnerabilities on your system that may affect guest users</li>
              <li>Privacy breaches that may occur when hosting games for others</li>
              <li>System instability that may impact other users' gaming experiences</li>
              <li>Data loss or corruption affecting guest users' game progress</li>
              <li>Any malicious software or vulnerabilities present on your computer</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>7.3 Expanded Indemnification</h3>
            <p className='col-span-full'>In addition to Main EULA indemnification, you agree to indemnify Company against:</p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Claims from other users regarding privacy violations</li>
              <li>Damages from malicious activities on your system</li>
              <li>Legal costs from game publisher licensing violations</li>
              <li>Security incidents affecting other network users</li>
              <li>Any regulatory violations related to your system's use</li>
              <li>Claims arising from third-party computer usage through Playcast Pavilion™</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>7.4 Third-Party User Protection</h3>
            <p className='col-span-full'>You acknowledge your responsibility to protect other users and agree to:</p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Maintain system security during hosted sessions</li>
              <li>Report any suspicious activity immediately</li>
              <li>Cooperate with security investigations</li>
              <li>Implement reasonable security measures on your system</li>
            </ul>

            <h2 className='col-span-full text-3xl'>8. Technical Requirements and Limitations</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>8.1 System Requirements</h3>
            <p className='col-span-full'>Playcast Pavilion™ requires:</p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Stable internet connection with minimum upload/download speeds</li>
              <li>Sufficient system resources to host multiple game sessions</li>
              <li>Compatible operating system and hardware configuration</li>
              <li>Administrative privileges for account and service management</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>8.2 Performance Expectations</h3>
            <p className='col-span-full'>You acknowledge that:</p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Hosting sessions may significantly impact your system performance</li>
              <li>Game installations may consume substantial storage space</li>
              <li>Network bandwidth may be fully utilized during peak periods</li>
              <li>System temperature and power consumption may increase</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>8.3 Compatibility Disclaimer</h3>
            <p className='col-span-full'>Company does not guarantee:</p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Compatibility with all games or gaming platforms</li>
              <li>Optimal performance on all hardware configurations</li>
              <li>Uninterrupted service availability</li>
              <li>Success of all hosted gaming sessions</li>
            </ul>

            <h2 className='col-span-full text-3xl'>9. Data Handling and Game Content</h2>

            <h3 className='col-span-full text-xl font-bold mt-4'>9.1 Game Content Management</h3>
            <p className='col-span-full'>Company may:</p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Download and install games from various digital platforms</li>
              <li>Manage game licenses and authentication</li>
              <li>Store game save data and user preferences</li>
              <li>Synchronize game content across the network</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>9.2 User Data Separation</h3>
            <p className='col-span-full'>You understand that:</p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Multiple user profiles may exist on your system simultaneously</li>
              <li>Game data for different users will be kept separate</li>
              <li>Company implements measures to prevent data cross-contamination</li>
              <li>You should not attempt to access other users' game data</li>
            </ul>

            <h2 className='col-span-full text-3xl'>10. Playcast Pavilion™-Specific Termination</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>10.1 Playcast Pavilion™ Termination</h3>
            <p className='col-span-full'>Company may terminate Playcast Pavilion™ services:</p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>For any violation of this Addendum</li>
              <li>For extended periods of non-participation</li>
              <li>For technical incompatibility issues</li>
              <li>At Company's sole discretion without cause</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>10.2 Effect of Playcast Pavilion™ Termination</h3>
            <p className='col-span-full'>Upon termination:</p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>All created accounts will be removed from your system</li>
              <li>Installed games will be uninstalled</li>
              <li>VPN access will be immediately revoked</li>
              <li>Your access to hosted games will cease</li>
              <li>Main EULA terms remain in effect for base software</li>
            </ul>

            <h2 className='col-span-full text-3xl'>11. Dispute Resolution and Governing Law</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>11.1 Arbitration</h3>
            <p className='col-span-full'>Any disputes arising from this Addendum or use of Playcast Pavilion™ are subject to the binding arbitration provisions in our Terms of Service, including the class action waiver. You agree to resolve disputes through individual arbitration as specified in our Terms of Service.</p>

            <h3 className='col-span-full text-xl font-bold mt-4'>11.2 Governing Law</h3>
            <p className='col-span-full'>This Addendum is governed by the laws of the State of Delaware, consistent with our Terms of Service.</p>

            <h2 className='col-span-full text-3xl'>12. Regulatory Compliance</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>12.1 Gaming License Compliance</h3>
            <p className='col-span-full'>You acknowledge that:</p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>All game access is subject to publisher licensing terms</li>
              <li>Geographic restrictions may apply to certain games</li>
              <li>Company's VPN infrastructure ensures license compliance</li>
              <li>Violations may result in legal action by game publishers</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>12.2 International Considerations</h3>
            <p className='col-span-full'>You agree to comply with:</p>

            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Local laws regarding distributed computing services</li>
              <li>International copyright and licensing regulations</li>
              <li>Export control laws for software and technology</li>
              <li>Privacy laws affecting cross-border data transfer</li>
            </ul>

            <h2 className='col-span-full text-3xl'>13. Acknowledgment of Playcast Pavilion™ Terms</h2>
            <p className='col-span-full'>By enabling Playcast Pavilion™ functionality, you acknowledge that you have read, understood, and agree to be bound by all terms in this Addendum in addition to the Main EULA and our Terms of Service. You understand the significant system access and resource sharing involved and consent to all described activities.</p>

            <p className='col-span-full'><strong>CRITICAL ACKNOWLEDGMENT</strong>: You specifically acknowledge that you understand the risks associated with distributed gaming, including playing games on other users' computers and hosting games for other users, and that Playcast disclaims responsibility for security, privacy, or other issues that may arise from this distributed model.</p>

            <h3 className='col-span-full text-xl font-bold mt-4'>Contact Information</h3>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>General support: support@playcast.io</li>
              <li>Privacy inquiries: privacy@playcast.io</li>
              <li>Address: 1000 SE Everett Mall Way #40p, Everett, WA 98208</li>
            </ul>

            <p className='col-span-full'><strong>Playcast Pavilion™ Addendum Version:</strong> 1.0</p>
            <p className='col-span-full'><strong>Last Updated:</strong> June 11th, 2025</p>

            <p className='col-span-full'><strong>TO ENABLE PLAYCAST PAVILION™:</strong> You must explicitly accept these additional terms by checking the "I agree to Playcast Pavilion™ Addendum terms" box during software installation or in the application settings.</p>
          </section>
        </div>
      </div>
    </div>
  ) : (
    <div className='bg-content text-content-text m-0 h-auto w-full p-0'>
      <div className='bg-hero bg-content text-content-text m-0 h-auto w-full bg-cover bg-no-repeat p-0'>
        <div className='container mx-auto h-auto w-full px-8 pb-8 pt-20'>
          <section id='hero' className='grid grid-cols-12 gap-x-4 gap-y-8'>
            <div className='grid-rows col-span-full grid content-center gap-y-4 lg:gap-y-8'>
              <h1 className='row-auto text-3xl font-bold lg:text-4xl'>End User License Agreement</h1>
              <p className='row-auto'>Last updated: June 12, 2025</p>
              <p className='row-auto'>Version: 1.0</p>
              <br />
            </div>
          </section>
        </div>
      </div>

      <div className='bg-content-secondary text-content-text m-0 h-auto w-full'>
        <div className='container mx-auto h-auto w-full px-0 py-20'>
          <section className='grid grid-cols-12 gap-10 md:gap-8'>
            <p className='col-span-full -mt-4'>
              <strong>Playcast Desktop Game Streaming Software</strong>
            </p>

            <p className='col-span-full -mt-4'>
              <strong>IMPORTANT - READ CAREFULLY:</strong> This End User License Agreement ("Agreement" or "EULA") is a legal agreement between
              you (either an individual or a single entity) ("You" or "User") and Playcast Inc. ("Company," "we," "us," or "our") for the
              Playcast desktop streaming software application, including any associated drivers, updates, and documentation (collectively,
              the "Software").
            </p>

            <p className='col-span-full -mt-4'>
              BY INSTALLING, COPYING, OR OTHERWISE USING THE SOFTWARE, YOU AGREE TO BE BOUND BY THE TERMS OF THIS AGREEMENT. IF YOU DO NOT
              AGREE TO THE TERMS OF THIS AGREEMENT, DO NOT INSTALL OR USE THE SOFTWARE.
            </p>

            <p className='col-span-full -mt-4'>
              <strong>IMPORTANT:</strong> Your use of Playcast's online Service is governed by our separate Terms of Service, which can be
              found at www.playcast.io. This EULA governs only the downloadable Software components.
            </p>

            <h2 className='col-span-full text-3xl'>1. License Grant and Eligibility</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>1.1 Age Requirement</h3>
            <p className='col-span-full'>
              You must be at least 18 years old to use the Software. By agreeing to this EULA, you represent and warrant that you are at
              least 18 years old and have not previously been suspended or removed from any Playcast services.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>1.2 Limited License</h3>
            <p className='col-span-full'>
              Subject to your compliance with this Agreement, Company grants you a limited, non-exclusive, non-transferable, revocable
              license to install and use the Software on devices you own or control solely for your personal, non-commercial use.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>1.3 Driver Installation</h3>
            <p className='col-span-full'>
              You acknowledge and agree that the Software requires the installation of system-level drivers and components to enable game
              and application streaming functionality. You expressly consent to the installation, operation, and automatic updating of
              these drivers.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>1.4 Restrictions</h3>
            <p className='col-span-full'>
              You may not:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Distribute, sublicense, rent, lease, or lend the Software</li>
              <li>Reverse engineer, decompile, or disassemble the Software</li>
              <li>Remove or modify any proprietary notices or labels</li>
              <li>Use the Software for commercial purposes without explicit written permission</li>
              <li>Circumvent or disable any security features or usage restrictions</li>
              <li>Use the Software in violation of our Terms of Service or for any illegal purpose</li>
            </ul>

            <h2 className='col-span-full text-3xl'>2. System Requirements and Compatibility</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>2.1 System Modifications</h3>
            <p className='col-span-full'>
              You acknowledge that the Software makes significant modifications to your computer system, including but not limited to:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Installation of kernel-level drivers</li>
              <li>Network configuration changes</li>
              <li>Graphics and audio system modifications</li>
              <li>Registry and system file modifications</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>2.2 Compatibility Disclaimer</h3>
            <p className='col-span-full'>
              Company does not guarantee compatibility with all hardware configurations, operating systems, or third-party software. You
              are responsible for ensuring your system meets minimum requirements.
            </p>

            <h2 className='col-span-full text-3xl'>3. Streaming and Content Responsibilities</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>3.1 Content Compliance</h3>
            <p className='col-span-full'>
              You are solely responsible for ensuring that any games, applications, or content you stream through the Software comply with:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>All applicable third-party End User License Agreements</li>
              <li>Copyright and intellectual property laws</li>
              <li>Platform-specific terms of service</li>
              <li>Local, state, and federal regulations</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>3.2 Third-Party EULA Compliance</h3>
            <p className='col-span-full'>
              You acknowledge that streaming games or applications may violate their respective EULAs. You agree to review and comply with
              all applicable third-party agreements before streaming any content.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>3.3 Prohibited Content</h3>
            <p className='col-span-full'>
              You may not use the Software to stream:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Illegal, harmful, or offensive content</li>
              <li>Content that violates third-party intellectual property rights</li>
              <li>Content that violates any applicable laws or regulations</li>
            </ul>

            <h2 className='col-span-full text-3xl'>4. Data Collection and Privacy</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>4.1 Data Collection</h3>
            <p className='col-span-full'>
              You acknowledge and consent to Company's collection, processing, and storage of:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>System performance and hardware information</li>
              <li>Usage statistics and streaming metrics</li>
              <li>Error logs and diagnostic information</li>
              <li>Network and connection data</li>
              <li>Game and application usage patterns</li>
              <li>Information described in our Privacy Policy</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>4.2 Analytics and Improvement</h3>
            <p className='col-span-full'>
              Company may use collected data to:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Improve Software performance and features</li>
              <li>Provide technical support</li>
              <li>Develop new features and services</li>
              <li>Generate anonymized analytics and reports</li>
              <li>Provide customized content and functionality</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>4.3 Privacy Policy</h3>
            <p className='col-span-full'>
              Your use of the Software is also governed by our Privacy Policy, which can be found at www.playcast.io and is incorporated
              herein by reference. The Privacy Policy explains how we collect, use, and protect your information, including automatic data
              collection and sharing with third-party services.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>4.4 International Data Transfers</h3>
            <p className='col-span-full'>
              You acknowledge that information collected by the Software may be stored and processed in the United States or other countries
              where Company or its service providers maintain facilities.
            </p>

            <h2 className='col-span-full text-3xl'>5. Prohibited Conduct</h2>
            <p className='col-span-full'>
              In addition to the restrictions in Section 1.4, you agree not to use the Software in any manner that violates the Prohibited
              Conduct provisions of our Terms of Service. This includes but is not limited to using the Software for illegal purposes,
              harassing other users, violating intellectual property rights, or interfering with the operation of our services. The complete
              list of prohibited conduct can be found in our Terms of Service at www.playcast.io.
            </p>

            <h2 className='col-span-full text-3xl'>6. Security and System Risks</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>6.1 Security Acknowledgment</h3>
            <p className='col-span-full'>
              You acknowledge that the Software:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Creates network connections that may expose your system to security risks</li>
              <li>Installs system-level components that may affect system security</li>
              <li>Enables remote access to your games and applications</li>
              <li>May introduce vulnerabilities inherent in streaming technology</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>6.2 Third-Party Computer Usage</h3>
            <p className='col-span-full'>
              <strong>IMPORTANT DISCLAIMER</strong>: When using Playcast's online services in conjunction with this Software, you understand
              and acknowledge that you may be playing games hosted on other users' personal computers. You expressly understand and agree that:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Playcast does not own, control, or monitor these third-party computers</li>
              <li>Playcast cannot guarantee the security, privacy, or integrity of third-party systems</li>
              <li>You assume all risks associated with accessing games on other users' computers</li>
              <li>Other users' computers may have different security configurations and vulnerabilities</li>
              <li>Playcast disclaims all responsibility for any security breaches, data loss, or other issues arising from use of third-party computers</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>6.3 Security Responsibility</h3>
            <p className='col-span-full'>
              You are solely responsible for:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Maintaining appropriate security measures on your system</li>
              <li>Monitoring and controlling access to your streaming sessions</li>
              <li>Protecting sensitive information during streaming</li>
              <li>Regular system updates and security patches</li>
              <li>Understanding the risks of distributed gaming platforms</li>
            </ul>

            <h2 className='col-span-full text-3xl'>7. Limitation of Liability and Indemnification</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>7.1 Disclaimer of Warranties</h3>
            <p className='col-span-full'>
              THE SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. COMPANY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED,
              INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>7.2 Limitation of Liability</h3>
            <p className='col-span-full'>
              IN NO EVENT SHALL COMPANY BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING
              BUT NOT LIMITED TO:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>System damage or data loss</li>
              <li>Security breaches or unauthorized access</li>
              <li>Third-party claims related to streamed content</li>
              <li>Business interruption or lost profits</li>
              <li>Hardware damage or malfunction</li>
            </ul>
            <p className='col-span-full'>
              COMPANY'S TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU FOR THE SOFTWARE IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>7.3 User Indemnification</h3>
            <p className='col-span-full'>
              You agree to indemnify, defend, and hold harmless Company from and against any claims, damages, losses, or expenses arising from:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Your use of the Software</li>
              <li>Your violation of this Agreement or our Terms of Service</li>
              <li>Your violation of third-party rights or agreements</li>
              <li>Content you stream through the Software</li>
              <li>Any system damage or security issues resulting from Software use</li>
            </ul>

            <h2 className='col-span-full text-3xl'>8. System Impact and User Acknowledgments</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>8.1 Performance Impact</h3>
            <p className='col-span-full'>
              You acknowledge that the Software may:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Affect system performance and resource usage</li>
              <li>Interfere with other applications or drivers</li>
              <li>Require significant network bandwidth</li>
              <li>Impact battery life on mobile devices</li>
            </ul>

            <h3 className='col-span-full text-xl font-bold mt-4'>8.2 System Changes</h3>
            <p className='col-span-full'>
              You acknowledge and accept responsibility for any system changes, including:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Driver conflicts or compatibility issues</li>
              <li>System instability or crashes</li>
              <li>Network configuration changes</li>
              <li>Registry or system file modifications</li>
            </ul>

            <h2 className='col-span-full text-3xl'>9. Automatic Updates</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>9.1 Update Consent</h3>
            <p className='col-span-full'>
              You agree that the Software may automatically download and install updates, including driver updates, without prior notice.
              These updates are subject to this Agreement.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>9.2 Update Requirements</h3>
            <p className='col-span-full'>
              Some updates may be required for continued use of the Software. Failure to install required updates may result in loss of
              functionality.
            </p>

            <h2 className='col-span-full text-3xl'>10. Termination</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>10.1 Termination Rights</h3>
            <p className='col-span-full'>
              This Agreement is effective until terminated. Company may terminate this Agreement immediately if you violate any terms or if
              your access to Playcast services is terminated under our Terms of Service. You may terminate by uninstalling the Software.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>10.2 Effect of Termination</h3>
            <p className='col-span-full'>
              Upon termination:
            </p>
            <ul className='col-span-10 col-start-2 -mt-4 list-disc'>
              <li>Your license rights immediately cease</li>
              <li>You must cease all use of the Software</li>
              <li>Certain data collection practices may continue as outlined in the Privacy Policy</li>
              <li>Installed drivers may remain unless manually removed</li>
            </ul>

            <h2 className='col-span-full text-3xl'>11. Third-Party Services and Integrations</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>11.1 Third-Party Dependencies</h3>
            <p className='col-span-full'>
              The Software may integrate with or depend on third-party services, platforms, or APIs. Company is not responsible for the
              availability, functionality, or terms of such third-party services.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>11.2 Platform Compliance</h3>
            <p className='col-span-full'>
              You are responsible for ensuring your use of the Software complies with the terms of service of any gaming platforms, digital
              distribution services, or other third-party services you access through the Software.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>11.3 Optional Features and Additional Agreements</h3>
            <p className='col-span-full'>
            The Software may include optional features that require acceptance of additional terms and conditions. Specifically, if you elect to enable Playcast Pavilion™ distributed gaming features, you must separately accept the Playcast Pavilion™ Software Addendum. These additional agreements supplement this EULA and are incorporated by reference when you enable the corresponding features.
            </p>

            <h2 className='col-span-full text-3xl'>12. Export Compliance</h2>
            <p className='col-span-full'>
              You acknowledge that the Software may be subject to export control laws and regulations. You agree to comply with all
              applicable export and import laws and regulations.
            </p>

            <h2 className='col-span-full text-3xl'>13. Dispute Resolution</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>13.1 Arbitration</h3>
            <p className='col-span-full'>
              Any disputes arising from this Agreement are subject to the dispute resolution and arbitration provisions set forth in our
              Terms of Service, including the binding arbitration requirement and class action waiver. The complete arbitration procedures
              can be found at www.playcast.io.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>13.2 Governing Law</h3>
            <p className='col-span-full'>
              This Agreement shall be governed by the laws of the State of Delaware, without regard to conflict of law principles,
              consistent with our Terms of Service.
            </p>

            <h2 className='col-span-full text-3xl'>14. General Provisions</h2>
            <h3 className='col-span-full text-xl font-bold mt-4'>14.1 Entire Agreement</h3>
            <p className='col-span-full'>
            This Agreement, together with our Terms of Service, Privacy Policy, and any applicable feature-specific addenda (such as the Playcast Pavilion™ Software Addendum), constitutes the entire agreement between the parties regarding the Software.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>14.2 Relationship to Terms of Service</h3>
            <p className='col-span-full'>
              This EULA supplements our Terms of Service. In case of conflicts between this EULA and the Terms of Service regarding the
              Software specifically, this EULA controls. For all other matters, the Terms of Service controls.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>14.3 Feature-Specific Addenda</h3>
            <p className='col-span-full'>
            Use of optional Software features may require acceptance of additional agreements. Such feature-specific addenda supplement this EULA and take precedence over this EULA for the specific features they govern.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>14.4 Severability</h3>
            <p className='col-span-full'>
              If any provision of this Agreement is found to be unenforceable, the remainder shall remain in full force and effect.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>14.5 No Waiver</h3>
            <p className='col-span-full'>
              Company's failure to enforce any provision shall not constitute a waiver of such provision or any other provision.
            </p>

            <h3 className='col-span-full text-xl font-bold mt-4'>14.6 Assignment</h3>
            <p className='col-span-full'>
              You may not assign this Agreement without Company's prior written consent. Company may assign this Agreement without restriction.
            </p>

            <h2 className='col-span-full text-3xl'>15. Contact Information</h2>
            <p className='col-span-full'>
              For questions regarding this Agreement, please contact:
            </p>
            <p className='col-span-full'>
              Playcast Inc.<br />
              1000 SE Everett Mall Way #40p<br />
              Everett, WA 98208<br />
              Email: support@playcast.io<br />
              Privacy-related inquiries: privacy@playcast.io
            </p>

            <h2 className='col-span-full text-3xl'>16. Acknowledgment</h2>
            <p className='col-span-full'>
              BY CLICKING "I AGREE" OR BY INSTALLING OR USING THE SOFTWARE, YOU ACKNOWLEDGE THAT YOU HAVE READ THIS AGREEMENT, UNDERSTAND
              IT, AND AGREE TO BE BOUND BY ITS TERMS AND CONDITIONS.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};